import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Context
import { useAuth } from './context/AuthContext';
import { useSystem } from './context/SystemContext';
import { useFireController } from './context/FireControllerContext';

// Config
import API_CONFIG from './config/apiConfig';

// Auth styles
import './components/Auth/Auth.css';

// Pages
import Dashboard from './pages/Dashboard/Dashboard';
import System from './pages/System/System';
import Location from './pages/Location/Location';
import Profile from './pages/Profile/Profile';
import SensorDetail from './pages/SensorDetail/SensorDetail';
import RelayDetail from './pages/RelayDetail/RelayDetail';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions/TermsAndConditions';
import CookiePolicy from './pages/CookiePolicy/CookiePolicy';
import ContactUs from './pages/ContactUs/ContactUs';

// Layout
import Layout from './components/Layout/Layout';
import PublicLayout from './components/PublicLayout/PublicLayout';

/**
 * AuthLoadingSpinner component - Shown while authentication or Fire Bridge
 * connection status is still being determined
 * @returns {JSX.Element} The rendered loading spinner
 */
const AuthLoadingSpinner = () => (
  <div className="auth-loading">
    <div className="auth-loading-spinner"></div>
    <p>Checking authentication...</p>
  </div>
);

/**
 * ProtectedRoute - Gates a page behind authentication and Fire Bridge connection
 * Shows a loading spinner while auth or connection status is pending, redirects
 * to "/" if the user is unauthenticated or the Fire Bridge connection failed,
 * and otherwise renders the page within Layout
 *
 * Defined at module scope so it has a stable component identity across
 * AppContent re-renders (e.g. on every polling update). A component defined
 * inside another component is recreated on every render, which causes React
 * to unmount and remount its subtree - wiping out any local state.
 *
 * @param {Object} props - Component properties
 * @param {boolean} props.authLoading - True while authentication status is being determined
 * @param {boolean} props.isAuthenticated - True if the user is authenticated
 * @param {boolean} props.isConnected - True if connected to the Fire Bridge
 * @param {boolean} props.connectionError - True if connecting to the Fire Bridge failed
 * @param {React.ReactNode} props.children - The page element to render once gated checks pass
 * @returns {JSX.Element} The rendered spinner, redirect, or page within Layout
 */
const ProtectedRoute = ({ authLoading, isAuthenticated, isConnected, connectionError, children }) => {
  if (!isAuthenticated || connectionError) {
    return authLoading ? <AuthLoadingSpinner /> : <Navigate to="/" />;
  }
  if (!isConnected) {
    return <AuthLoadingSpinner />;
  }
  return <Layout>{children}</Layout>;
};

/**
 * AppContent component - Handles routing and automatic connection to Fire Bridge
 * This component uses the SystemContext which is provided by the parent App component
 *
 * Authentication Flow:
 * 1. User must authenticate with Keycloak first
 * 2. After authentication, automatically connects to Fire Bridge using environment variable
 * 3. Polling service uses authenticated token for API requests
 *
 * @returns {JSX.Element} The rendered application routes
 */
function AppContent() {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const { getToken, isAuthenticated, isLoading: authLoading, login, user, authError } = useAuth();
  const { initializePolling } = useSystem();
  const { initialize: initializeFireController, fetchFireControllers } = useFireController();
  const location = useLocation();

  // Public routes that don't require authentication
  const publicRoutes = ['/', '/privacy-policy', '/terms-and-conditions', '/cookie-policy', '/contact-us'];

  // Track when auth service becomes unavailable
  useEffect(() => {
    if (authError) {
      setServiceUnavailable(true);
      setShowErrorToast(true);
      // Clear the login intent flag since Keycloak is down
      sessionStorage.removeItem('attemptLogin');
    }
  }, [authError]);

  // Check if user attempted login before page reload
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !authError) {
      const attemptLogin = sessionStorage.getItem('attemptLogin');
      if (attemptLogin === 'true') {
        sessionStorage.removeItem('attemptLogin');
        login();
      }
    }
  }, [authLoading, isAuthenticated, authError, login]);

  /**
   * Handle login button click
   * Always reload the page to ensure Keycloak is available before attempting login
   * This catches cases where Keycloak goes down after initial page load
   */
  const handleLogin = () => {
    // Set flag to remember login intent across page reload
    sessionStorage.setItem('attemptLogin', 'true');
    // Reload to re-initialize Keycloak and verify availability
    // If Keycloak is up, the page will reload and auto-redirect to login
    // If Keycloak is down, the error toast will appear
    window.location.reload();
  };

  /**
   * Dismiss the error toast
   */
  const dismissToast = () => {
    setShowErrorToast(false);
  };

  /**
   * Automatically connect to Fire Bridge after authentication
   * Also initializes Fire Controller service and fetches user's Fire Controllers
   */
  useEffect(() => {
    if (isAuthenticated && !isConnected && !connectionError) {
      try {
        // Use Keycloak token getter function for authentication
        // Token is dynamically fetched before each API call, supporting automatic refresh
        initializePolling(API_CONFIG.BASE_URL, getToken);

        // Initialize Fire Controller service with same base URL and token getter
        initializeFireController(API_CONFIG.BASE_URL, getToken);

        // Fetch Fire Controllers for the authenticated user
        // Use 'sub' (subject) as the userId - Keycloak's unique user identifier
        if (user && user.sub) {
          fetchFireControllers(user.sub);
        }

        setIsConnected(true);
        console.log(`Auto-connected to Fire Bridge at ${API_CONFIG.BASE_URL} with Keycloak authentication`);
      } catch (error) {
        console.error('Failed to connect to Fire Bridge:', error);
        alert('Failed to connect to the Fire Bridge');
        setConnectionError(true);
      }
    }
  }, [isAuthenticated, isConnected, connectionError, getToken, initializePolling, initializeFireController, fetchFireControllers, user]);

  /**
   * Redirect to Keycloak login if not authenticated and not on a public route
   */
  useEffect(() => {
    const isPublicRoute = publicRoutes.includes(location.pathname);
    if (!authLoading && !isAuthenticated && !isPublicRoute) {
      login();
    }
  }, [authLoading, isAuthenticated, login, location.pathname, publicRoutes]);

  // Shared gating props passed to every ProtectedRoute
  const protectedRouteProps = { authLoading, isAuthenticated, isConnected, connectionError };

  return (
    <div className="App">
      <Routes>
        {/* Public routes - accessible without authentication */}
        <Route
          path="/privacy-policy"
          element={
            <PublicLayout>
              <PrivacyPolicy />
            </PublicLayout>
          }
        />
        <Route
          path="/terms-and-conditions"
          element={
            <PublicLayout>
              <TermsAndConditions />
            </PublicLayout>
          }
        />
        <Route
          path="/cookie-policy"
          element={
            <PublicLayout>
              <CookiePolicy />
            </PublicLayout>
          }
        />
        <Route
          path="/contact-us"
          element={
            <PublicLayout>
              <ContactUs />
            </PublicLayout>
          }
        />
        {/* Protected routes - require authentication and Fire Bridge connection */}
        <Route
          path="/"
          element={
            authLoading ? (
              <div className="auth-loading">
                <div className="auth-loading-spinner"></div>
                <p>Checking Authentication...</p>
              </div>
            ) : !isAuthenticated || connectionError ? (
              <PublicLayout>
                <div className="auth-required">
                  {showErrorToast && (
                    <div className="auth-toast-error">
                      <span className="auth-toast-icon">⚠️</span>
                      <span className="auth-toast-message">
                        Authentication service is currently unavailable. Please try again later.
                      </span>
                      <button
                        className="auth-toast-close"
                        onClick={dismissToast}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  {connectionError && (
                    <div className="auth-toast-error">
                      <span className="auth-toast-icon">⚠️</span>
                      <span className="auth-toast-message">
                        Unable to connect to the Fire Bridge. Please try again.
                      </span>
                      <button
                        className="auth-toast-close"
                        onClick={() => setConnectionError(false)}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <div className="auth-card">
                    <h2>Welcome</h2>
                    <p>
                      {connectionError
                        ? 'Reload the page to retry connecting to the Fire Bridge'
                        : 'Please log in to access the application'}
                    </p>
                    <button onClick={handleLogin} className="modern-login-button">
                      {connectionError ? 'Reload' : 'Login'}
                    </button>
                  </div>
                </div>
              </PublicLayout>
            ) : (
              <Navigate to="/dashboard" />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute {...protectedRouteProps}>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/system"
          element={
            <ProtectedRoute {...protectedRouteProps}>
              <System />
            </ProtectedRoute>
          }
        />
        <Route
          path="/location"
          element={
            <ProtectedRoute {...protectedRouteProps}>
              <Location />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute {...protectedRouteProps}>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/system/sensor/:sensorName"
          element={
            <ProtectedRoute {...protectedRouteProps}>
              <SensorDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/system/relay/:relayId"
          element={
            <ProtectedRoute {...protectedRouteProps}>
              <RelayDetail />
            </ProtectedRoute>
          }
        />
      </Routes>
    </div>
  );
}

export default AppContent;
